/**
 * Voice Recorder Component
 *
 * Provides voice recording functionality with OpenAI Whisper transcription.
 * Click to start recording, click again to stop.
 * Audio is sent to backend for transcription, NOT stored.
 */

import { useState, useRef, useCallback } from 'react'
import { Mic, Loader2, Square } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { toast } from 'sonner'
import { apiClient } from '@/lib/api-client'

interface VoiceRecorderProps {
  onTranscription: (text: string) => void
  disabled?: boolean
}

export function VoiceRecorder({ onTranscription, disabled }: VoiceRecorderProps) {
  const [isRecording, setIsRecording] = useState(false)
  const [isTranscribing, setIsTranscribing] = useState(false)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const streamRef = useRef<MediaStream | null>(null)

  const transcribe = useCallback(async (audioBlob: Blob) => {
    setIsTranscribing(true)
    try {
      const formData = new FormData()
      formData.append('audio', audioBlob, 'recording.webm')

      const response = await apiClient.post<{ text: string }>(
        '/api/v1/transcribe/',
        formData
      )

      if (response.text && response.text.trim()) {
        onTranscription(response.text.trim())
        toast.success('Transcription added')
      } else {
        toast.info('No speech detected')
      }
    } catch (error) {
      console.error('Transcription failed:', error)
      toast.error('Failed to transcribe audio')
    } finally {
      setIsTranscribing(false)
    }
  }, [onTranscription])

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm',
      })
      mediaRecorderRef.current = mediaRecorder
      chunksRef.current = []

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data)
        }
      }

      mediaRecorder.onstop = async () => {
        // Stop all tracks to release microphone
        stream.getTracks().forEach((track) => track.stop())

        if (chunksRef.current.length === 0) {
          toast.error('No audio recorded')
          return
        }

        const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm' })

        // Check if recording is too short (< 0.5 seconds usually means empty)
        if (audioBlob.size < 1000) {
          toast.error('Recording too short')
          return
        }

        await transcribe(audioBlob)
      }

      mediaRecorder.start()
      setIsRecording(true)
      toast.info('Recording... Click to stop')
    } catch (error) {
      console.error('Failed to start recording:', error)
      if (error instanceof DOMException && error.name === 'NotAllowedError') {
        toast.error('Microphone access denied. Please allow microphone access in your browser settings.')
      } else {
        toast.error('Failed to start recording')
      }
    }
  }, [transcribe])

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop()
    }
    setIsRecording(false)
  }, [])

  const toggleRecording = useCallback(async () => {
    if (isRecording) {
      stopRecording()
    } else {
      await startRecording()
    }
  }, [isRecording, startRecording, stopRecording])

  const isDisabled = disabled || isTranscribing

  function renderButtonContent(): React.ReactNode {
    if (isTranscribing) {
      return (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Transcribing...</span>
        </>
      )
    }
    if (isRecording) {
      return (
        <>
          <Square className="h-4 w-4 fill-current" />
          <span className="animate-pulse">Stop Recording</span>
        </>
      )
    }
    return (
      <>
        <Mic className="h-4 w-4" />
        <span>Record</span>
      </>
    )
  }

  const recordingStyles = isRecording
    ? 'border-red-500 text-red-500 hover:bg-red-50 dark:hover:bg-red-950'
    : ''

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={toggleRecording}
      disabled={isDisabled}
      className={`gap-2 ${recordingStyles}`}
    >
      {renderButtonContent()}
    </Button>
  )
}
